import { Link, LinkProps as ChakraLinkProps } from "@chakra-ui/react";
import { isFunction } from "lodash-es";
import React, { useCallback, useContext, useState } from "react";
import {
  Link as ReactRouterLink,
  matchRoutes,
  To,
  UNSAFE_DataRouterContext,
} from "react-router-dom";
import { isValidUrl } from "shared/utils/UrlUtils";

export interface RouteLinkProps extends ChakraLinkProps {
  to: To;
  prefetch?: boolean;
}

export const RouteLink: React.FC<RouteLinkProps> = React.forwardRef<
  HTMLAnchorElement,
  RouteLinkProps
>(({ to, prefetch, ...props }, ref) => {
  const [didRunLoaders, setDidRunLoaders] = useState(false);

  const dataRouterContext = useContext(UNSAFE_DataRouterContext);
  const routes = dataRouterContext?.router.routes;

  // https://github.com/remix-run/react-router/discussions/8012
  const runLoaders = useCallback(async () => {
    if (!prefetch || typeof to !== "string" || didRunLoaders) {
      return;
    }
    setDidRunLoaders(true);
    const matches = matchRoutes(routes ?? [], to);
    await Promise.all(
      matches?.map((match) => {
        const loaderFn = isFunction(match.route.loader)
          ? match.route.loader
          : undefined;
        const value = loaderFn?.({
          request: new Request(to),
          params: match.params,
        });
        return value instanceof Promise ? value : Promise.resolve(value);
      }) ?? [],
    );
  }, [didRunLoaders, prefetch, to, routes]);

  return (
    <Link
      ref={ref}
      onMouseOverCapture={runLoaders}
      {...(isValidUrl(to)
        ? { href: to, target: "_blank" }
        : { as: ReactRouterLink, to })}
      {...props}
    />
  );
});

RouteLink.displayName = "RouteLink";
